import { Request, Response, RequestHandler } from 'express';
import { rateLimit } from 'express-rate-limit';
import { rateLimitConfigs } from '../configs/rateLimitConfigs';
import { RateLimitOptions } from '../types/types';
import { logger } from '../logger/logger';
import { configManager } from '../configs/configManager';

let apiLimiter: RequestHandler | null = null;
let authLimiter: RequestHandler | null = null;
let sensitiveLimiter: RequestHandler | null = null;

export const createLimiter = (options: RateLimitOptions): RequestHandler =>
  rateLimit({
    ...options,
    skipSuccessfulRequests: false,
    handler: (req: Request, res: Response) => {
      logger.warn(`Rate limit exceeded for ${req.ip} on ${req.originalUrl}`);
      return res.status(429).json({
        success: false,
        error: options.errorName || 'Rate Limit Exceeded',
        message: options.message || 'Too many requests, please try again later.'
      });
    }
  });

export const initializeRateLimiters = async (): Promise<void> => {
  try {
    const config = await configManager.getConfig();
    const rateLimitConfig = rateLimitConfigs[config?.isDevelopment ? 'development' : 'production'];

    apiLimiter = createLimiter({
      ...rateLimitConfig.api,
      errorName: 'API Rate Limit Exceeded',
      message: 'Too many requests, please try again later.'
    });

    authLimiter = createLimiter({
      ...rateLimitConfig.auth,
      errorName: 'Authentication Rate Limit Exceeded',
      message: 'Too many authentication attempts, please try again later.'
    });

    sensitiveLimiter = createLimiter({
      ...rateLimitConfig.sensitive,
      errorName: 'Operation Rate Limit Exceeded',
      message: 'Too many sensitive operations attempted, please try again later.'
    });

    logger.info('Rate limiters initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize rate limiters:', error);
    throw error;
  }
};

export const getApiLimiter = (): RequestHandler => {
  if (!apiLimiter) throw new Error('Rate limiters not initialized. Call initializeRateLimiters() first.');
  return apiLimiter;
};

export const getAuthLimiter = (): RequestHandler => {
  if (!authLimiter) throw new Error('Rate limiters not initialized. Call initializeRateLimiters() first.');
  return authLimiter;
};

export const getSensitiveLimiter = (): RequestHandler => {
  if (!sensitiveLimiter) throw new Error('Rate limiters not initialized. Call initializeRateLimiters() first.');
  return sensitiveLimiter;
};
